import { flattenError } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

/** Maximum number of recipients for a test email. */
export const MAX_EMAILS = 10;

/**
 * Parses the comma-separated test email string into a list of addresses,
 * trimming surrounding whitespace from each address.
 *
 * @param testEmail - The raw comma-separated value from the email addresses field.
 * @returns The list of addresses, or an empty list when the field is undefined.
 */
export function parseTestEmailList(testEmail?: string): string[] {
  return testEmail?.split(',').map((email) => email.trim()) ?? [];
}

/**
 * Validates the full test email field for the "Send test email" action: the
 * list must be present, within the address limit, and every entry a valid email.
 *
 * @param testEmail - The raw comma-separated value from the email addresses field.
 * @returns A flat list of error messages, empty when valid.
 */
export function validateTestEmail(testEmail?: string): string[] {
  const error = validate.single(parseTestEmailList(testEmail), {
    presence: { allowEmpty: false, message: '^Email addresses cannot be empty.' },
    length: {
      maximum: MAX_EMAILS,
      tooLong: `^You can only add up to ${MAX_EMAILS} email addresses.`,
    },
    list: {
      email: {
        message: '^An email address is invalid.',
      },
    },
  });
  return flattenError(error) ?? [];
}

/**
 * Validates only the test email address limit for wizard navigation. Presence
 * and email format are intentionally omitted so the (optional) test step stays
 * skippable when the field is empty, while still blocking navigation once the
 * user has entered more than the allowed number of addresses.
 *
 * @param testEmail - The raw comma-separated value from the email addresses field.
 * @returns A flat list of error messages, empty when within the limit.
 */
export function validateTestEmailCount(testEmail?: string): string[] {
  const error = validate.single(parseTestEmailList(testEmail), {
    length: {
      maximum: MAX_EMAILS,
      tooLong: `^You can only add up to ${MAX_EMAILS} email addresses.`,
    },
  });
  return error ?? [];
}

/**
 * Validates the example contact selection for the "Send test email" action.
 *
 * @param testPersonId - The selected example contact's id, if any.
 * @returns A flat list of error messages, empty when a contact is selected.
 */
export function validateTestPerson(testPersonId?: string): string[] {
  const error = validate.single(testPersonId, {
    presence: { allowEmpty: false, message: '^Example contact cannot be empty.' },
  });
  return error ?? [];
}
